import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface POSOrder {
  id: string;
  orderNumber: string;
  customer: string;
  items: number;
  total: number;
  paymentMethod: string;
  status: "completed" | "pending" | "in_progress" | "cancelled";
  date: string;
  time: string;
  cashier: string;
  terminal: string;
}

const MOCK_POS_ORDERS: POSOrder[] = [
  { id: "1", orderNumber: "POS-2024-001", customer: "John Doe", items: 3, total: 125.99, paymentMethod: "Card", status: "completed", date: "2024-01-15", time: "10:30 AM", cashier: "Maria G.", terminal: "Terminal 1" },
  { id: "2", orderNumber: "POS-2024-002", customer: "Jane Smith", items: 5, total: 289.50, paymentMethod: "Cash", status: "completed", date: "2024-01-15", time: "11:15 AM", cashier: "Carlos R.", terminal: "Terminal 2" },
  { id: "3", orderNumber: "POS-2024-003", customer: "Guest", items: 2, total: 45.00, paymentMethod: "Card", status: "in_progress", date: "2024-01-15", time: "12:00 PM", cashier: "Maria G.", terminal: "Terminal 1" },
  { id: "4", orderNumber: "POS-2024-004", customer: "Mike Johnson", items: 1, total: 89.99, paymentMethod: "Card", status: "pending", date: "2024-01-15", time: "02:30 PM", cashier: "Ana L.", terminal: "Terminal 3" },
  { id: "5", orderNumber: "POS-2024-005", customer: "Sarah Wilson", items: 4, total: 156.75, paymentMethod: "Cash", status: "completed", date: "2024-01-14", time: "04:45 PM", cashier: "Maria G.", terminal: "Terminal 1" },
  { id: "6", orderNumber: "POS-2024-006", customer: "Guest", items: 2, total: 67.00, paymentMethod: "Card", status: "cancelled", date: "2024-01-14", time: "05:20 PM", cashier: "Carlos R.", terminal: "Terminal 2" },
];

export default function POSOrdersSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [terminalFilter, setTerminalFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredOrders = MOCK_POS_ORDERS.filter((order) => {
    const matchesSearch = order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.cashier.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    const matchesTerminal = terminalFilter === "all" || order.terminal === terminalFilter;
    return matchesSearch && matchesStatus && matchesTerminal;
  });

  const getStatusBadge = (status: POSOrder["status"]) => {
    const styles = {
      completed: "bg-emerald-500/20 text-emerald-500",
      pending: "bg-amber-500/20 text-amber-500",
      in_progress: "bg-blue-500/20 text-blue-500",
      cancelled: "bg-red-500/20 text-red-500",
    };
    const labels = {
      completed: t("posOrders.statusCompleted"),
      pending: t("posOrders.statusPending"),
      in_progress: t("posOrders.statusInProgress"),
      cancelled: t("posOrders.statusCancelled"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const todayOrders = MOCK_POS_ORDERS.filter((o) => o.date === "2024-01-15").length;
  const completedOrders = MOCK_POS_ORDERS.filter((o) => o.status === "completed").length;
  const inProgressOrders = MOCK_POS_ORDERS.filter((o) => o.status === "in_progress").length;
  const todaySales = MOCK_POS_ORDERS.filter((o) => o.date === "2024-01-15" && o.status === "completed")
    .reduce((acc, o) => acc + o.total, 0);

  const terminals = [...new Set(MOCK_POS_ORDERS.map((o) => o.terminal))];

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posMenu.posOrders")}</h1>
          <p className={themeClasses.textSecondary}>{t("posOrders.posOrdersSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("posOrders.newOrder")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.todayOrders")}</span>
            <i className="ri-shopping-bag-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{todayOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.completedOrders")}</span>
            <i className="ri-checkbox-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>{completedOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.inProgress")}</span>
            <i className="ri-loader-4-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-blue-500`}>{inProgressOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.todaySales")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>${todaySales.toFixed(2)}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posOrders.searchPOSPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={terminalFilter}
              onChange={(e) => setTerminalFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posOrders.allTerminals")}</option>
              {terminals.map((terminal) => (
                <option key={terminal} value={terminal}>{terminal}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posOrders.allStatuses")}</option>
              <option value="completed">{t("posOrders.statusCompleted")}</option>
              <option value="pending">{t("posOrders.statusPending")}</option>
              <option value="in_progress">{t("posOrders.statusInProgress")}</option>
              <option value="cancelled">{t("posOrders.statusCancelled")}</option>
            </select>

            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-calendar-line"></i>
              {t("posOrders.dateRange")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.order")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.customer")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.cashier")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.terminal")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.items")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.total")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.payment")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{order.orderNumber}</p>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{order.date} {order.time}</p>
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>{order.customer}</td>
                  <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{order.cashier}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs bg-gray-500/20 ${themeClasses.text}`}>
                      {order.terminal}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-center ${themeClasses.text}`}>{order.items}</td>
                  <td className={`px-4 py-3 text-right font-bold ${themeClasses.text}`}>${order.total.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-center ${themeClasses.textSecondary}`}>{order.paymentMethod}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(order.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-eye-line"></i>
                      </button>
                      <button aria-label={t("general.print", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-printer-line"></i>
                      </button>
                      {order.status === "pending" && (
                        <button className="p-2 rounded-lg hover:bg-emerald-500/10 text-emerald-500">
                          <i className="ri-check-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
